import { useState } from 'react';
import { ExternalLink, Heart, Pencil } from 'lucide-react';
import { useSiteData, usePageContent } from '@/context/SiteDataContext';
import { useEditMode } from '@/context/EditModeContext';
import EditText from '@/components/editable/EditText';
import PageHero from '@/components/PageHero';
import SmartLink from '@/components/SmartLink';
import Modal from '@/components/editable/Modal';
import LinkListPanel from '@/components/editable/LinkListEditor';
import { defaultGivingLinks } from '@/data/linkDefaults';

const DEFAULT_FUNDS = [
  { name: 'General Fund',      desc: 'Supports the ongoing ministry, operations, and mission of the jurisdiction.' },
  { name: 'Missions',          desc: 'Fuels local and international mission work through the Church of God in Christ.' },
  { name: 'Youth Department',  desc: 'Empowers the next generation with resources, retreats, and mentorship.' },
  { name: 'Building Fund',     desc: 'Advances construction, renovation, and expansion projects.' },
  { name: 'Special Offerings', desc: 'Designated giving for conferences, guest speakers, and special initiatives.' },
];

export default function Giving() {
  const { settings = {} } = useSiteData();
  const c = usePageContent('giving');
  const s = settings || {};
  const giveUrl = s.givelify_url || 'https://www.givelify.com/donate/wisconsin-first-ecclesiastical-jurisdiction';
  const { editMode } = useEditMode();
  const [editLinks, setEditLinks] = useState(false);
  const givingDefaults = defaultGivingLinks(s.givelify_url);
  const givingLinks = (s.giving_links || givingDefaults).filter(l => l.visible !== false);

  return (
    <div>
      <PageHero slug="giving" overline="Sow into the Kingdom" heading="Giving" defaultImage="https://images.unsplash.com/photo-1600423809757-8cd9d249bd63?w=2400"
        text={c.hero_verse || '"Every man according as he purposeth in his heart, so let him give; not grudgingly, or of necessity: for God loveth a cheerful giver." — 2 Corinthians 9:7'}>
        <div className="mt-8 flex flex-wrap justify-center gap-4" data-testid="giving-links">
          {givingLinks.map((l, i) => (
            <SmartLink key={i} href={l.href} data-testid={`giving-link-${i}`} className={i === 0 ? 'btn-gold' : 'btn-outline-white'}>
              <Heart className="w-5 h-5" /> {l.label}
            </SmartLink>
          ))}
          {editMode && (
            <button onClick={() => setEditLinks(true)} data-testid="edit-giving-links-btn" className="inline-flex items-center gap-1 text-xs px-3 py-1.5 rounded-full bg-gold text-royal-dark font-bold">
              <Pencil className="w-3 h-3" /> Edit giving links
            </button>
          )}
        </div>
      </PageHero>
      {editLinks && (
        <Modal title="Giving Links" onClose={() => setEditLinks(false)} testid="giving-links-modal">
          <LinkListPanel settingsKey="giving_links" defaults={givingDefaults} testid="giving-links-editor"
            help="Edit the names and destinations of the giving buttons. Drag to reorder." onSaved={() => setEditLinks(false)} />
        </Modal>
      )}

      <section className="bg-white py-20">
        <div className="max-w-6xl mx-auto px-6">
          <EditText field="funds_overline" target="page:giving" as="div" className="overline mb-3">{c.funds_overline || 'Ways to Give'}</EditText>
          <EditText field="funds_heading" target="page:giving" as="h2" className="font-serif text-3xl lg:text-4xl text-royal font-bold mb-10 gold-underline">{c.funds_heading || 'Designated Funds'}</EditText>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {DEFAULT_FUNDS.map(f => (
              <div key={f.name} className="card-tactile p-6">
                <div className="w-10 h-10 rounded-full bg-gold/10 text-gold flex items-center justify-center mb-4"><Heart className="w-5 h-5" /></div>
                <h3 className="font-serif text-xl text-royal font-bold">{f.name}</h3>
                <p className="text-charcoal-muted text-sm mt-2 leading-relaxed">{f.desc}</p>
                <a href={giveUrl} target="_blank" rel="noreferrer" data-testid={`give-fund-${f.name.replace(/\s/g, '-').toLowerCase()}`} className="mt-4 inline-flex items-center gap-1 text-sm text-royal font-semibold hover:text-gold">Give to {f.name} <ExternalLink className="w-3 h-3" /></a>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section id="give-by-mail" className="bg-[#F8F9FA] py-20">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <EditText field="mail_overline" target="page:giving" as="div" className="overline mb-3">{c.mail_overline || 'Other Ways'}</EditText>
          <EditText field="mail_heading" target="page:giving" as="h2" className="font-serif text-3xl text-royal font-bold mb-6">{c.mail_heading || 'Give by Mail'}</EditText>
          <p className="text-charcoal-muted text-lg">
            <EditText field="site_name" target="settings" as="span">{s.site_name || 'Wisconsin First Ecclesiastical Jurisdiction'}</EditText><br />
            <EditText field="address_line1" target="settings" as="span">{s.address_line1 || '2020 W. Fond du Lac Ave'}</EditText><br />
            <EditText field="address_line2" target="settings" as="span">{s.address_line2 || 'Milwaukee, WI 53206'}</EditText>
          </p>
          <EditText field="mail_note" target="page:giving" as="p" multiline className="text-sm text-charcoal-muted mt-6">
            {c.mail_note || 'Please make checks payable to Wisconsin First Ecclesiastical Jurisdiction. Include the fund designation in the memo line.'}
          </EditText>
        </div>
      </section>
    </div>
  );
}
